import { decryptToken, isEncryptedToken } from './encryption';

export interface OverlayParams {
  access_token?: string;
  width?: string;
  height?: string;
  refresh_frequency?: string;
  show_count?: string;
  show_count_extended?: string;
  theme?: string;
  opacity?: string;
  state?: string;
  animation_enabled?: string;
  animation_frequency?: string;
  animation_duration?: string;
  animation_text_template?: string;
}

export interface NotificationParams {
  notifications_enabled?: string;
  notifications_interval?: string;
  notifications_animation?: string;
  notifications_sound?: string;
  notifications_theme?: string;
  notifications_donation_text?: string;
  notifications_challenge_text?: string;
}

export const parseUrlParams = async (): Promise<OverlayParams & NotificationParams> => {
  const urlParams = new URLSearchParams(window.location.search);
  
  let accessToken = urlParams.get('access_token') || undefined;
  
  // Try to decrypt the token if it looks encrypted
  if (accessToken && isEncryptedToken(accessToken)) {
    try {
      accessToken = await decryptToken(accessToken);
    } catch (error) {
      console.warn('Failed to decrypt access token, using as-is:', error);
    }
  }
  
  return {
    access_token: accessToken,
    width: urlParams.get('w') || urlParams.get('width') || undefined,
    height: urlParams.get('h') || urlParams.get('height') || undefined,
    refresh_frequency: urlParams.get('refresh_frequency') || urlParams.get('refresh') || undefined,
    show_count: urlParams.get('show_count') || urlParams.get('count') || undefined,
    show_count_extended: urlParams.get('show_count_extended') || urlParams.get('count_extended') || undefined,
    theme: urlParams.get('theme') || undefined,
    opacity: urlParams.get('opacity') || urlParams.get('op') || undefined,
    state: urlParams.get('state') || undefined,
    animation_enabled: urlParams.get('animation_enabled') || undefined,
    animation_frequency: urlParams.get('animation_frequency') || undefined,
    animation_duration: urlParams.get('animation_duration') || undefined,
    animation_text_template: urlParams.get('animation_text_template') || undefined,
    notifications_enabled: urlParams.get('notifications_enabled') || undefined,
    notifications_interval: urlParams.get('notifications_interval') || undefined,
    notifications_animation: urlParams.get('notifications_animation') || undefined,
    notifications_sound: urlParams.get('notifications_sound') || undefined,
    notifications_theme: urlParams.get('notifications_theme') || undefined,
    notifications_donation_text: urlParams.get('notifications_donation_text') || undefined,
    notifications_challenge_text: urlParams.get('notifications_challenge_text') || undefined,
  };
};

export const generateOverlayUrl = async (params: OverlayParams, baseUrl?: string): Promise<string> => {
  const currentUrl = baseUrl || window.location.origin + window.location.pathname;
  const url = new URL(currentUrl);
  
  // Clear existing params
  url.search = '';
  
  // Add new params
  if (params.access_token) {
    // Encrypt the access token for security
    try {
      const { encryptToken } = await import('./encryption');
      const encryptedToken = await encryptToken(params.access_token);
      url.searchParams.set('access_token', encryptedToken);
    } catch (error) {
      console.warn('Failed to encrypt access token, using unencrypted:', error);
      url.searchParams.set('access_token', params.access_token);
    }
  }
  if (params.width) {
    url.searchParams.set('w', params.width);
  }
  if (params.height) {
    url.searchParams.set('h', params.height);
  }
  if (params.refresh_frequency) {
    url.searchParams.set('refresh_frequency', params.refresh_frequency);
  }
  if (params.show_count) {
    url.searchParams.set('show_count', params.show_count);
  }
  if (params.show_count_extended) {
    url.searchParams.set('show_count_extended', params.show_count_extended);
  }
  if (params.theme) {
    url.searchParams.set('theme', params.theme);
  }
  if (params.opacity) {
    url.searchParams.set('opacity', params.opacity);
  }
  if (params.state) {
    url.searchParams.set('state', params.state);
  }
  if (params.animation_enabled) {
    url.searchParams.set('animation_enabled', params.animation_enabled);
  }
  if (params.animation_frequency) {
    url.searchParams.set('animation_frequency', params.animation_frequency);
  }
  if (params.animation_duration) {
    url.searchParams.set('animation_duration', params.animation_duration);
  }
  if (params.animation_text_template) {
    url.searchParams.set('animation_text_template', params.animation_text_template);
  }
  
  return url.toString();
};

export const generateNotificationUrl = async (params: NotificationParams, baseUrl?: string): Promise<string> => {
  const currentUrl = baseUrl || window.location.origin + '/notifications';
  const url = new URL(currentUrl);
  
  // Clear existing params
  url.search = '';
  
  // Add notification params
  if (params.notifications_enabled !== undefined) {
    url.searchParams.set('notifications_enabled', params.notifications_enabled);
  }
  if (params.notifications_interval !== undefined) {
    url.searchParams.set('notifications_interval', params.notifications_interval);
  }
  if (params.notifications_animation !== undefined) {
    url.searchParams.set('notifications_animation', params.notifications_animation);
  }
  if (params.notifications_sound !== undefined) {
    url.searchParams.set('notifications_sound', params.notifications_sound);
  }
  if (params.notifications_theme !== undefined) {
    url.searchParams.set('notifications_theme', params.notifications_theme);
  }
  if (params.notifications_donation_text !== undefined) {
    url.searchParams.set('notifications_donation_text', params.notifications_donation_text);
  }
  if (params.notifications_challenge_text !== undefined) {
    url.searchParams.set('notifications_challenge_text', params.notifications_challenge_text);
  }
  
  return url.toString();
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    } catch (fallbackErr) {
      document.body.removeChild(textArea);
      return false;
    }
  }
};
